import { InfoTooltip } from "@impero-com/common/components/infoTooltip/tooltip";
import { TFunction } from "@impero-com/common/utils/i18n";
import { createColumnHelper } from "@tanstack/react-table";
import { Link } from "react-router-dom";

import {
    ControlWithoutFieldsGraphItemDto,
    ControlWithoutFieldsResources,
} from "api/generated/model";
import { formatControlSchedulingType } from "org/control/utils";
import { tstArrIncludesSomeFilterFn } from "utils/table/tstArrIncludesSomeFilterFn";

import { WebRoute } from "../../../../../routes";

import * as theme from "./BulkControlsTable.css";
import {
    BULK_CONTROLS_CONTROL_PROGRAM_COLUMN_ID,
    BULK_CONTROLS_ESTIMATED_ACTIVITIES_COLUMN_ID,
    BULK_CONTROLS_SCHEDULING_INTERVAL_COLUMN_ID,
    BULK_CONTROLS_TITLE_COLUMN_ID,
} from "./consts";

export const bulkControlsTableColumnHelper =
    createColumnHelper<ControlWithoutFieldsGraphItemDto>();

export function getBulkControlsTitleColumn(t: TFunction) {
    return bulkControlsTableColumnHelper.accessor((row) => row.title, {
        id: BULK_CONTROLS_TITLE_COLUMN_ID,
        header: t("TestingControlView_BulkAdd_TitleColumn"),
        enableColumnFilter: false,
        cell: (info) => (
            <Link
                to={`${WebRoute.CONTROLS}/${info.row.original.id}`}
                target="_blank"
            >
                {info.getValue()}
            </Link>
        ),
    });
}

export function getBulkControlsProgramColumn(
    t: TFunction,
    resources: ControlWithoutFieldsResources
) {
    return bulkControlsTableColumnHelper.accessor(
        (row) =>
            resources.controlPrograms.find(
                (p) => p.resource.id === row.controlProgramId
            )!.resource.name,
        {
            id: BULK_CONTROLS_CONTROL_PROGRAM_COLUMN_ID,
            cell: (info) => info.getValue(),
            filterFn: tstArrIncludesSomeFilterFn,
            header: t("TestingControlView_BulkAdd_ControlProgramColumn"),
        }
    );
}

export function getBulkControlsSchedulingColumn(t: TFunction) {
    return bulkControlsTableColumnHelper.accessor(
        (row) =>
            row.scheduling
                ? formatControlSchedulingType(row.scheduling.rule.type, t)
                : t("TestingControlView_BulkAdd_NoScheduling"),
        {
            id: BULK_CONTROLS_SCHEDULING_INTERVAL_COLUMN_ID,
            cell: (info) => info.getValue(),
            filterFn: tstArrIncludesSomeFilterFn,
            header: t("TestingControlView_BulkAdd_IntervalColumn"),
        }
    );
}

export function getBulkControlsEstimatedActivitiesColumn(
    t: TFunction,
    estimationMap: Map<string, number>
) {
    return bulkControlsTableColumnHelper.display({
        id: BULK_CONTROLS_ESTIMATED_ACTIVITIES_COLUMN_ID,
        header: () => (
            <div className={theme.headerContainer}>
                {t("TestingControlView_BulkAdd_EstimatedActivities")}
                <InfoTooltip
                    text={t("TestingControlView_EstimatedActivities_Info")}
                    iconClassName={theme.headerInfoIcon}
                />
            </div>
        ),
        cell: (info) => {
            const value = estimationMap.get(info.row.original.id);
            return value ? value.toString() : "0";
        },
    });
}
